'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import HealthBadge from '@/components/ui/HealthBadge';
import BVODetailPanel from './BVODetailPanel';

type HealthStatus = 'healthy' | 'at-risk' | 'critical' | 'recovering' | 'optimized';
type RiskLevel = 'none' | 'low' | 'medium' | 'high' | 'critical';

interface BVO {
  id: string;
  name: string;
  type: string;
  healthScore: number;
  conformance: number;
  cycleTimeP95: string;
  valueAtRisk: string;
  riskLevel: RiskLevel;
  status: HealthStatus;
  owner: string;
  activeCases: number;
  openRisks: number;
  lastUpdated: string;
  bvsCount: number;
}

const bvoData: BVO[] = [
  { id: 'bvo-001', name: 'Financial Close', type: 'Finance', healthScore: 82, conformance: 91, cycleTimeP95: '4.2d', valueAtRisk: '$0', riskLevel: 'none', status: 'healthy', owner: 'Anika Kowalski', activeCases: 1, openRisks: 0, lastUpdated: '2 min ago', bvsCount: 8 },
  { id: 'bvo-002', name: 'Cash Application', type: 'Finance', healthScore: 61, conformance: 74, cycleTimeP95: '3.8d', valueAtRisk: '$2.4M', riskLevel: 'high', status: 'at-risk', owner: 'James Whitfield', activeCases: 847, openRisks: 4, lastUpdated: '4 min ago', bvsCount: 7 },
  { id: 'bvo-003', name: 'Customer Order', type: 'Operations', healthScore: 44, conformance: 63, cycleTimeP95: '8.1d', valueAtRisk: '$5.1M', riskLevel: 'critical', status: 'critical', owner: 'Marcus Osei', activeCases: 2341, openRisks: 7, lastUpdated: '1 min ago', bvsCount: 10 },
  { id: 'bvo-004', name: 'Supplier Invoice', type: 'Procurement', healthScore: 78, conformance: 87, cycleTimeP95: '5.3d', valueAtRisk: '$340K', riskLevel: 'low', status: 'recovering', owner: 'Priya Nair', activeCases: 1205, openRisks: 2, lastUpdated: '6 min ago', bvsCount: 6 },
  { id: 'bvo-005', name: 'Inventory Health', type: 'Operations', healthScore: 91, conformance: 95, cycleTimeP95: '2.1d', valueAtRisk: '$0', riskLevel: 'none', status: 'optimized', owner: 'Elena Vasquez', activeCases: 312, openRisks: 0, lastUpdated: '3 min ago', bvsCount: 5 },
  { id: 'bvo-006', name: 'Working Capital', type: 'Finance', healthScore: 56, conformance: 69, cycleTimeP95: '12.4d', valueAtRisk: '$8.7M', riskLevel: 'high', status: 'at-risk', owner: 'James Whitfield', activeCases: 94, openRisks: 3, lastUpdated: '5 min ago', bvsCount: 9 },
  { id: 'bvo-007', name: 'Production Order', type: 'Manufacturing', healthScore: 74, conformance: 82, cycleTimeP95: '3.6d', valueAtRisk: '$1.2M', riskLevel: 'medium', status: 'recovering', owner: 'Chen Wei', activeCases: 678, openRisks: 2, lastUpdated: '9 min ago', bvsCount: 7 },
  { id: 'bvo-008', name: 'Revenue Performance', type: 'Finance', healthScore: 88, conformance: 93, cycleTimeP95: '1.8d', valueAtRisk: '$0', riskLevel: 'none', status: 'healthy', owner: 'Anika Kowalski', activeCases: 156, openRisks: 0, lastUpdated: '11 min ago', bvsCount: 6 },
  { id: 'bvo-009', name: 'Treasury Operations', type: 'Finance', healthScore: 70, conformance: 79, cycleTimeP95: '2.9d', valueAtRisk: '$620K', riskLevel: 'medium', status: 'recovering', owner: 'Fatima Al-Hassan', activeCases: 43, openRisks: 1, lastUpdated: '14 min ago', bvsCount: 5 },
  { id: 'bvo-010', name: 'Tax Compliance', type: 'Compliance', healthScore: 85, conformance: 90, cycleTimeP95: '6.7d', valueAtRisk: '$0', riskLevel: 'low', status: 'healthy', owner: 'Sophie Brennan', activeCases: 28, openRisks: 1, lastUpdated: '18 min ago', bvsCount: 4 },
  { id: 'bvo-011', name: 'HR Onboarding', type: 'HR', healthScore: 67, conformance: 76, cycleTimeP95: '9.3d', valueAtRisk: '$280K', riskLevel: 'medium', status: 'at-risk', owner: 'Marcus Osei', activeCases: 89, openRisks: 2, lastUpdated: '22 min ago', bvsCount: 5 },
  { id: 'bvo-012', name: 'Customer Returns', type: 'Operations', healthScore: 52, conformance: 68, cycleTimeP95: '11.2d', valueAtRisk: '$1.8M', riskLevel: 'high', status: 'at-risk', owner: 'Priya Nair', activeCases: 423, openRisks: 3, lastUpdated: '31 min ago', bvsCount: 6 },
];

const riskConfig: Record<RiskLevel, { label: string; className: string }> = {
  none: { label: 'None', className: 'bg-positive/10 text-positive' },
  low: { label: 'Low', className: 'bg-primary/10 text-primary' },
  medium: { label: 'Medium', className: 'bg-warning/10 text-warning' },
  high: { label: 'High', className: 'bg-negative/15 text-negative' },
  critical: { label: 'Critical', className: 'bg-negative/25 text-negative font-semibold' },
};

type SortField = keyof BVO;

export default function BVOTableSection() {
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortField, setSortField] = useState<SortField>('healthScore');
  const [sortAsc, setSortAsc] = useState(true);
  const [selectedBVO, setSelectedBVO] = useState<BVO | null>(null);
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const types = ['all', ...Array.from(new Set(bvoData.map((b) => b.type)))];
  const statuses = ['all', 'healthy', 'at-risk', 'critical', 'recovering', 'optimized'];

  const filtered = bvoData
    .filter((b) => {
      const matchSearch = b.name.toLowerCase().includes(search.toLowerCase()) || b.owner.toLowerCase().includes(search.toLowerCase());
      const matchType = typeFilter === 'all' || b.type === typeFilter;
      const matchStatus = statusFilter === 'all' || b.status === statusFilter;
      return matchSearch && matchType && matchStatus;
    })
    .sort((a, b) => {
      const av = a[sortField];
      const bv = b[sortField];
      if (typeof av === 'number' && typeof bv === 'number') return sortAsc ? av - bv : bv - av;
      return sortAsc ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
    });

  const totalPages = Math.ceil(filtered.length / pageSize);
  const paginated = filtered.slice((page - 1) * pageSize, page * pageSize);

  function toggleSort(field: SortField) {
    if (sortField === field) setSortAsc((p) => !p);
    else { setSortField(field); setSortAsc(true); }
  }

  function toggleRow(id: string) {
    setSelectedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAll() {
    if (selectedRows.size === paginated.length) setSelectedRows(new Set());
    else setSelectedRows(new Set(paginated.map((b) => b.id)));
  }

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <Icon name="ChevronUpDownIcon" size={12} className="text-muted-foreground" />;
    return <Icon name={sortAsc ? 'ChevronUpIcon' : 'ChevronDownIcon'} size={12} className="text-primary" />;
  };

  return (
    <div className="flex gap-6">
      {/* Main table */}
      <div className={`flex-1 min-w-0 transition-all duration-300 ${selectedBVO ? 'xl:max-w-[calc(100%-440px)]' : ''}`}>
        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="flex items-center gap-2 bg-input border border-border rounded-lg px-3 py-2 flex-1 min-w-[200px] max-w-xs">
            <Icon name="MagnifyingGlassIcon" size={14} className="text-muted-foreground shrink-0" />
            <input
              type="text"
              placeholder="Search BVOs or owners..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="bg-transparent text-sm text-foreground placeholder:text-muted-foreground outline-none flex-1"
            />
          </div>

          <select
            value={typeFilter}
            onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }}
            className="bg-input border border-border rounded-lg px-3 py-2 text-sm text-foreground outline-none cursor-pointer hover:border-primary/40 transition-colors"
          >
            {types.map((t) => (
              <option key={`type-filter-${t}`} value={t}>{t === 'all' ? 'All Types' : t}</option>
            ))}
          </select>

          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            className="bg-input border border-border rounded-lg px-3 py-2 text-sm text-foreground outline-none cursor-pointer hover:border-primary/40 transition-colors"
          >
            {statuses.map((s) => (
              <option key={`status-filter-${s}`} value={s}>{s === 'all' ? 'All Statuses' : s.charAt(0).toUpperCase() + s.slice(1)}</option>
            ))}
          </select>

          <div className="ml-auto flex items-center gap-2">
            <span className="text-xs text-muted-foreground">{filtered.length} BVOs</span>
            <button className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground bg-muted px-3 py-2 rounded-lg border border-border transition-colors">
              <Icon name="ArrowDownTrayIcon" size={12} />
              Export
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="bg-card rounded-xl card-glow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="w-10 px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selectedRows.size === paginated.length && paginated.length > 0}
                      onChange={toggleAll}
                      className="rounded border-border accent-primary"
                    />
                  </th>
                  {[
                    { key: 'name', label: 'BVO Name' },
                    { key: 'type', label: 'Type' },
                    { key: 'healthScore', label: 'Health' },
                    { key: 'conformance', label: 'Conformance' },
                    { key: 'cycleTimeP95', label: 'Cycle P95' },
                    { key: 'valueAtRisk', label: 'Value at Risk' },
                    { key: 'riskLevel', label: 'Risk Level' },
                    { key: 'status', label: 'Status' },
                    { key: 'activeCases', label: 'Active Cases' },
                    { key: 'owner', label: 'Owner' },
                  ].map((col) => (
                    <th
                      key={`col-${col.key}`}
                      onClick={() => toggleSort(col.key as SortField)}
                      className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide cursor-pointer hover:text-foreground transition-colors select-none whitespace-nowrap"
                    >
                      <div className="flex items-center gap-1.5">
                        {col.label}
                        <SortIcon field={col.key as SortField} />
                      </div>
                    </th>
                  ))}
                  <th className="px-4 py-3 w-20" />
                </tr>
              </thead>
              <tbody>
                {paginated.map((bvo, idx) => (
                  <tr
                    key={bvo.id}
                    onClick={() => setSelectedBVO(selectedBVO?.id === bvo.id ? null : bvo)}
                    className={`border-b border-border cursor-pointer transition-colors group ${
                      selectedBVO?.id === bvo.id
                        ? 'bg-primary/5 border-l-2 border-l-primary'
                        : idx % 2 === 0 ? 'hover:bg-muted/30' : 'bg-muted/10 hover:bg-muted/30'
                    }`}
                  >
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedRows.has(bvo.id)}
                        onChange={() => toggleRow(bvo.id)}
                        className="rounded border-border accent-primary"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium text-foreground group-hover:text-primary transition-colors">{bvo.name}</p>
                        <p className="text-2xs text-muted-foreground">{bvo.bvsCount} BVS linked</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded">{bvo.type}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-16 h-1.5 bg-muted rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full"
                            style={{
                              width: `${bvo.healthScore}%`,
                              background: bvo.healthScore >= 80 ? 'var(--positive)' : bvo.healthScore >= 60 ? 'var(--warning)' : 'var(--negative)',
                            }}
                          />
                        </div>
                        <span className="text-xs font-semibold font-tabular text-foreground">{bvo.healthScore}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-semibold font-tabular ${
                        bvo.conformance >= 85 ? 'text-positive' : bvo.conformance >= 70 ? 'text-warning' : 'text-negative'
                      }`}>
                        {bvo.conformance}%
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs font-tabular text-foreground">{bvo.cycleTimeP95}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-semibold font-tabular ${bvo.valueAtRisk === '$0' ? 'text-positive' : 'text-negative'}`}>
                        {bvo.valueAtRisk}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded font-medium ${riskConfig[bvo.riskLevel].className}`}>
                        {riskConfig[bvo.riskLevel].label}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <HealthBadge status={bvo.status} size="sm" />
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs font-tabular text-foreground">{bvo.activeCases.toLocaleString()}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <div className="w-5 h-5 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center text-2xs font-semibold text-primary-foreground shrink-0">
                          {bvo.owner.charAt(0)}
                        </div>
                        <span className="text-xs text-muted-foreground truncate max-w-[80px]">{bvo.owner.split(' ')[0]}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button title="View details" className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors">
                          <Icon name="EyeIcon" size={13} />
                        </button>
                        <button title="Edit BVO" className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors">
                          <Icon name="PencilSquareIcon" size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between px-4 py-3 border-t border-border">
            <p className="text-xs text-muted-foreground">
              Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, filtered.length)} of {filtered.length}
            </p>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-1.5 rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <Icon name="ChevronLeftIcon" size={14} />
              </button>
              {Array.from({ length: totalPages }).map((_, i) => (
                <button
                  key={`page-btn-${i + 1}`}
                  onClick={() => setPage(i + 1)}
                  className={`w-7 h-7 rounded-lg text-xs font-medium transition-colors ${
                    page === i + 1 ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  }`}
                >
                  {i + 1}
                </button>
              ))}
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="p-1.5 rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <Icon name="ChevronRightIcon" size={14} />
              </button>
            </div>
          </div>
        </div>

        {/* Bulk action bar */}
        {selectedRows.size > 0 && (
          <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-card border border-border rounded-xl shadow-2xl px-5 py-3 flex items-center gap-4 z-50 animate-slide-up">
            <span className="text-sm font-medium text-foreground">{selectedRows.size} selected</span>
            <div className="h-4 w-px bg-border" />
            <button className="text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1.5">
              <Icon name="ArrowDownTrayIcon" size={12} />
              Export
            </button>
            <button className="text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1.5">
              <Icon name="TagIcon" size={12} />
              Tag
            </button>
            <button className="text-xs text-negative hover:text-negative/80 transition-colors flex items-center gap-1.5">
              <Icon name="TrashIcon" size={12} />
              Archive
            </button>
            <button
              onClick={() => setSelectedRows(new Set())}
              className="p-1 rounded text-muted-foreground hover:text-foreground transition-colors"
            >
              <Icon name="XMarkIcon" size={14} />
            </button>
          </div>
        )}
      </div>

      {/* Detail panel */}
      {selectedBVO && (
        <div className="w-[420px] shrink-0">
          <BVODetailPanel bvo={selectedBVO} onClose={() => setSelectedBVO(null)} />
        </div>
      )}
    </div>
  );
}